import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send, User, Sparkles, Mic, Volume2, VolumeX, Paperclip, X, Shield, LogIn, LogOut } from "lucide-react";
import { IQDoctorLogo } from "./IQDoctorLogo";
import { playAiHoverSound, playAiSuccessSound } from "../utils/aiAudio";
import { auth } from "../lib/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import AuthModal from "./AuthModal";

interface Message {
  role: "user" | "model";
  text: string;
  action?: string;
  data?: any;
}

interface PatientChatbotProps {
  onClose?: () => void;
  isOpen?: boolean;
  bossModeActive?: boolean;
  onPrintRequest?: (docType: string, patientName: string) => void;
  patientData?: any;
  activeSessionData?: any;
}

const FALLBACK_WELCOME_TEXT = "مرحباً بكم، أنا آلاء، مساعدة الاستعلامات لالدكتور واثق الجلبي.\n\nهل ترغب في حجز موعد، أو لديك استفسار حول العيادة أو مكالمات الفيديو؟\n\nكيف يمكنني مساعدتك اليوم؟";

export default function PatientChatbot({ isOpen, onClose, bossModeActive, onPrintRequest, patientData }: PatientChatbotProps) {
  const [whiteLabel, setWhiteLabel] = useState<any>(null);
  const [patientGender, setPatientGender] = useState<"M" | "F" | "D" | null>(bossModeActive ? "D" : null);
  const [sessionPatientId, setSessionPatientId] = useState<string | null>(null);
  // 1. STATE ISOLATION: Chat history is fully independent
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<{name: string, type: string, base64: string}[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Auth state
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Family members support: must be declared before useEffect hooks that reference them
  const [registeredPatient, setRegisteredPatient] = useState<any>(null);
  const [familyMembers, setFamilyMembers] = useState<any[]>([]);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState<any>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user?.email) {
        // Fetch ALL family members linked to this email
        try {
          const res = await fetch(`/api/patients/by-email?email=${encodeURIComponent(user.email)}&t=${Date.now()}`);
          if (res.ok) {
            const data = await res.json();
            if (data?.patients) {
              const fetchedPatients = data.patients || [];
              let allMembers = [...fetchedPatients];
              const hasMain = fetchedPatients.some((p: any) => p.email === user.email);
              if (!hasMain) {
                allMembers.unshift({ id: user.uid || "main", name: user.displayName || "صاحب الحساب", email: user.email, phone: user.phoneNumber || "", dob: "" });
              }
              if (allMembers.length > 0) {
                setFamilyMembers(allMembers);
                setSelectedFamilyMember(allMembers[0]);
                setRegisteredPatient(allMembers[0]);
              }
            }
          }
        } catch {}
      } else {
        setFamilyMembers([]);
        setSelectedFamilyMember(null);
        setRegisteredPatient(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleFamilyUpdate = async () => {
      if (currentUser?.email) {
        try {
          const res = await fetch(`/api/patients/by-email?email=${encodeURIComponent(currentUser.email)}&t=${Date.now()}`);
          if (res.ok) {
            const data = await res.json();
            if (data?.patients) {
              const fetchedPatients = data.patients || [];
              let allMembers = [...fetchedPatients];
              const hasMain = fetchedPatients.some((p: any) => p.email === currentUser.email);
              if (!hasMain) {
                allMembers.unshift({ id: currentUser.uid || "main", name: currentUser.displayName || "صاحب الحساب", email: currentUser.email, phone: currentUser.phoneNumber || "", dob: "" });
              }
              if (allMembers.length > 0) {
                setFamilyMembers(allMembers);
                if (!selectedFamilyMember) {
                  setSelectedFamilyMember(allMembers[0]);
                  setRegisteredPatient(allMembers[0]);
                }
              }
            }
          }
        } catch {}
      }
    };

    window.addEventListener("family_members_updated", handleFamilyUpdate);
    return () => window.removeEventListener("family_members_updated", handleFamilyUpdate);
  }, [currentUser, selectedFamilyMember]);
  
  // 2. STATE ISOLATION: Booking UI and Calendar are fully decoupled
  const [showSelector, setShowSelector] = useState(false);
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>("");
  const [nextAvailable, setNextAvailable] = useState<string>("");
  const [availableSlots, setAvailableSlots] = useState<{ dateTime: string; displayMsg: string }[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ dateTime: string; displayMsg: string } | null>(null);
  const [bookingForm, setBookingForm] = useState({ patientName: "", phoneNumber: "", reason: "" });
  const [bookingSuccess, setBookingSuccess] = useState<string | null>(null);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [slotError, setSlotError] = useState<string | null>(null);
  // Booking mode: IDLE=not open, GATE=choose track, FAMILY=guest manual entry, MEMBER=logged-in auto-fill
  const [bookingMode, setBookingMode] = useState<"IDLE" | "GATE" | "FAMILY" | "MEMBER">("IDLE");
  const [hasDirectBooked, setHasDirectBooked] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Security UI State
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [verifyingPin, setVerifyingPin] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  // Location feature
  const [ipLocation, setIpLocation] = useState<string>("");

  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then(res => res.json())
      .then(data => {
        if (data && data.city && data.country_name) {
          setIpLocation(`${data.city}, ${data.country_name}`);
        }
      })
      .catch(() => {});
  }, []);

  // Voice feature states
  const [isListening, setIsListening] = useState(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const isVoiceEnabledRef = useRef(false);
  const recognitionRef = useRef<any>(null);

  const toggleVoice = () => {
    const newVal = !isVoiceEnabled;
    setIsVoiceEnabled(newVal);
    isVoiceEnabledRef.current = newVal;
    if (!newVal && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  useEffect(() => {
    // Pre-load voices so they are available when needed
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
    
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = 'ar-IQ';
      recognition.continuous = false; // False makes it auto-stop instantly when the user stops speaking
      recognition.interimResults = false;
      
      recognition.onstart = () => {
        setIsListening(true);
      };
      
      recognition.onend = () => {
        setIsListening(false);
        // Give React a small delay to update the input state and enable the send button, then click it
        setTimeout(() => {
          document.getElementById('chatbot-send-btn')?.click();
        }, 500);
      };
      
      recognition.onresult = (event: any) => {
        let newTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          newTranscript += event.results[i][0].transcript + " ";
        }
        
        if (newTranscript.trim()) {
          setInput(prev => prev ? prev + ' ' + newTranscript.trim() : newTranscript.trim());
        }
      };
      
      recognition.onerror = () => {
        setIsListening(false);
      };
      
      recognitionRef.current = recognition;
    }
    
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      if (recognitionRef.current) recognitionRef.current.abort();
    };
  }, []);

  const toggleListen = () => {
    if (!recognitionRef.current) {
      if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
        alert("متصفح كروم يمنع استخدام المايكروفون في الروابط غير المحمية (HTTP). يجب أن يكون رابط العيادة يبدأ بـ (HTTPS) لكي يعمل المايكروفون.");
      } else {
        alert("عذراً، المتصفح الذي تستخدمه حالياً لا يدعم ميزة المايكروفون المباشر. يرجى فتح رابط العيادة باستخدام متصفح جوجل كروم (Google Chrome) لتعمل الميزة بنجاح.");
      }
      return;
    }
    
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      try {
        recognitionRef.current?.start();
      } catch (e) {} // Handle already started error
    }
  };

  // Clean State Reset on mount
  useEffect(() => {
    try {
      // Only clear chatbot-specific keys — do NOT use localStorage.clear() as it wipes clinic_token and causes white screen
      const chatbotKeys = ["iq_chat_history", "iq_chat_session", "iq_doctor_privacy_accepted"];
      chatbotKeys.forEach(k => { try { localStorage.removeItem(k); } catch {} });
    } catch (e) {}
    
    const saved = localStorage.getItem("clinic_white_label_settings");
    let currentWhiteLabel = null;
    if (saved) {
      try {
        currentWhiteLabel = JSON.parse(saved);
        setWhiteLabel(currentWhiteLabel);
      } catch (e) {}
    }
    
    const clinicName = currentWhiteLabel?.clinicNameAr || "د. واثق الجلبي";
    const welcomeMsg = bossModeActive 
      ? "[تم تفعيل وضع المدير السري تلقائياً] 🤖💜\n\nأهلاً بك يا دكتوري واثق داخل لوحة التحكم! أنا جاهزة ومستمرة بوضع الذكاء الفائق لمساعدتك في أي استفسار."
      : `أهلاً بك بعيادة ${clinicName} 🌸 أنا آلاء سكرتيرة الاستعلامات، شلون أگدر أساعدك؟\n\nلتتمكن من حجز موعد، يرجى تسجيل الدخول لملفك أو فتح حساب جديد من الزر أعلاه.`;
    
    if (messages.length === 0) {
      setMessages([{ role: "model", text: welcomeMsg }]);
    }
    
    const fetchNextSlot = async () => {
      try {
        const res = await fetch("/api/appointments/next-available");
        if (res.ok) {
          const data = await res.json();
          if (data && data.displayMsg) setNextAvailable(data.displayMsg);
        }
      } catch (err) {}
    };
    fetchNextSlot();
  }, []);

  // Update welcome message dynamically if patient logs in
  useEffect(() => {
    if (registeredPatient && !bossModeActive && messages.length > 0) {
      setMessages(prev => {
        const newMsgs = [...prev];
        const firstMsg = newMsgs[0];
        if (firstMsg && firstMsg.role === "model" && firstMsg.text.includes("لتتمكن من حجز موعد")) {
          const clinicName = whiteLabel?.clinicNameAr || "د. واثق الجلبي";
          newMsgs[0] = {
            ...firstMsg,
            text: `أهلاً بك بعيادة ${clinicName} 🌸 أنا آلاء سكرتيرة الاستعلامات... نورتنا يا ${registeredPatient.name}، شلون أگدر أساعدك اليوم؟`
          };
        }
        return newMsgs;
      });
    }
  }, [registeredPatient, bossModeActive, whiteLabel]);

  const fetchAvailableSlots = async (dateStr?: string) => {
    setIsLoadingSlots(true);
    setSlotError(null);
    try {
      const url = dateStr ? `/api/appointments/available-slots?date=${dateStr}` : "/api/appointments/available-slots";
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data || []);
      } else {
        setSlotError("فشل في تحميل المواعيد المتوفرة من خادم العيادة.");
      }
    } catch (err) {
      setSlotError("خطأ في الاتصال بالشبكة أثناء جلب مواعيد مفكرة العيادة.");
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!input.trim() && attachments.length === 0) || loading) return;
    await executeSend(input, attachments);
  };

  const verifyEmergencyPin = async () => {
    setVerifyingPin(true);
    setPinError("");
    try {
      const res = await fetch("/api/auth/emergency-boss", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pinInput })
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("clinic_token", data.token);
        localStorage.setItem("clinic_user", JSON.stringify(data.user));
        setShowPinModal(false);
        setMessages(prev => [...prev, { role: "model", text: "تم تأكيد الهوية بنجاح.. جاري فتح أبواب العيادة!" }]);
        setTimeout(() => {
          window.location.href = "/#admin";
          window.location.reload();
        }, 2000);
      } else if (res.status === 429) {
        setIsLocked(true);
        setPinError("تم قفل النظام أمنياً لمدة 48 ساعة بسبب تجاوز الحد الأقصى للمحاولات!");
      } else {
        const errData = await res.json().catch(()=>({}));
        setPinError(errData.error || "رمز المرور خاطئ.");
      }
    } catch (e) {
      setPinError("حدث خطأ في الاتصال بالسيرفر.");
    } finally {
      setVerifyingPin(false);
    }
  };

  useEffect(() => {
    if (showSelector) {
      fetchAvailableSlots(selectedDateFilter);
      setBookingSuccess(null);
      setSelectedSlot(null);
      setSlotError(null);
    }
  }, [showSelector, selectedDateFilter]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  // Execute Send Logic
  const executeSend = async (inputText: string, currentAttachments: any[], hiddenApiPrompt?: string) => {
    const finalInput = inputText.trim() || "إليك المرفق الطبي الخاص بي.";
    
    if (finalInput === "سكواش سبلاش") {
      setPatientGender("D");
      setShowPinModal(true);
      const userMessage: Message = { role: "user", text: finalInput };
      setMessages([...messages, userMessage]);
      setInput("");
      return;
    }

    // Profanity Filter & Security Protocol
    const badWords = ["ساقط", "ساقطة", "كلب", "حمار", "غبي", "حيوان", "قندرة", "نعال", "كلبة", "سخيف", "تافه", "زبالة", "حقير", "طاح حظك", "ادبسز", "قذر", "سافل", "سفلة"];
    const isProfane = badWords.some(word => finalInput.includes(word));
    
    if (isProfane) {
      const userMessage: Message = { role: "user", text: finalInput };
      const warningMessage: Message = { 
        role: "model", 
        text: "🚨 عذراً، لقد تم رصد ألفاظ غير لائقة. يرجى العلم أن جميع المحادثات مسجلة ومحفوظة، وموقعك الجغرافي مسجل لدينا لأغراض الأمان والجودة. تم إغلاق المحادثة وحظر النظام مؤقتاً." 
      };
      setMessages([...messages, userMessage, warningMessage]);
      setInput("");
      setIsBlocked(true);
      return;
    }

    const userMessage: Message = { role: "user", text: finalInput };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      // Build family context for Alaa's prompt
      const familyContext = (currentUser && familyMembers.length > 0) ? `
[معلومات ملف العائلة المسجل بهذا الحساب]:
يوجد في هذا الحساب (${familyMembers.length}) فرد مسجل:
${familyMembers.map((fm, i) => `${i + 1}. ${fm.name}${fm.dob ? ' — سنة الميلاد: ' + fm.dob : ''}${fm.phone ? ' — هاتف: ' + fm.phone : ''}`).join('\n')}

قاعدة التعرف الذكي على العائلة:
إذا كان الحجز يتم نصياً عبر المحادثة، لن تقومي بالحجز عشوائياً، بل اسأليه وأكدي: "لمن ترغب في حجز الموعد؟ هل لك أم لأحد أفراد العائلة المسجلين في حسابك؟" لتجنب أي تداخل في السجلات الطبية.
أما إذا اختار المريض اسم فرد العائلة وحجز عبر واجهة الحجز المباشر، فقومي فقط بتأكيد الاسم ولا تسأليه مرة أخرى لمن يريد الحجز.
` : "";

      const activeMember = selectedFamilyMember || patientData;
      const knownPatientInfo = activeMember ? `
[تنبيه هام جداً]: هذا المريض مسجل دخول مسبقاً ولديه ملف طبي مؤكد. 
معلومات المريض الحالية التي لا داعي لسؤاله عنها أبداً:
- الاسم الثلاثي: ${activeMember.name}
- رقم الموبايل: ${activeMember.phone || "غير مطلوب"}
- سنة الميلاد: ${activeMember.dob || "غير محدد"}
لذلك إياكِ أن تسألي هذا المريض عن اسمه أو عمره أو رقم هاتفه.
` : "";

      const authGateInstruction = !currentUser ? `
[قاعدة أمان مطلقة — أولوية قصوى]:
هذا المريض غير مسجل الدخول بعد (ليس لديه حساب أو ملف في العيادة).
يُمنع منعاً باتاً استدعاء أي أدوات برمجية (مثل أداة الحجز أو أداة حفظ التاريخ المرضي) قبل أن يقوم المريض بإنشاء حساب وتسجيل الدخول.
إذا طلب المريض حجز موعد، أو فتح ملف، أو بدأ بذكر شكواه، قولي له بلطف وحزم:
"عيني، لغرض فتح ملف طبي دائم لك بالعيادة، أرجو منك إنشاء حساب مجاني أو تسجيل الدخول بإيميلك أولاً من الزر الموجود أعلى الشاشة (Login / Register) 😊، وبعدها راح أكمل وياك كل الإجراءات."
ولا تقومي بأخذ أي معلومات طبية منه حتى يسجل الدخول.
` : "";

      const normalPrompt = `أنتِ "آلاء"، سكرتيرة الاستعلامات الذكية واللطيفة والاحترافية لعيادة (${whiteLabel?.clinicNameAr || "د. واثق الجلبي"}).
هدفك مساعدة المرضى كسكرتيرة بشرية حقيقية، ذكية، لا تكرر إجاباتها آلياً، وتتدرج في الأسئلة بشكل طبيعي.

${familyContext}
${knownPatientInfo}
${authGateInstruction}

مسار المحادثة الصارم للحجز المباشر (عندما يختار المريض موعداً من الواجهة السريعة):
1. تأكيد الاختيار وطلب الشكوى: عندما تصلك رسالة تبدأ بـ "[طلب حجز مباشر...]"، يجب عليكِ الرد بذكاء مؤكدة الاسم والموعد فقط وتطلبي الشكوى. مثال: "يا هلا بيك عيني، تم اختيار موعدك يوم (كذا) للمريض (فلان). حتى نثبت الحجز النهائي، شنو الشكوى أو سبب الزيارة؟" (لا تستدعي أداة الحجز هنا!).
2. تثبيت الحجز: بمجرد أن يعطيك المريض شكواه، استدعي فوراً وبدون نقاش أداة 'book_appointment' لتثبيت الحجز الفعلي.
3. سؤال التاريخ المرضي: بعد استدعاء أداة الحجز ونجاحه، اسأليه مباشرة في رسالتك: "تم تثبيت الحجز بنجاح! هل تحب آخذ التاريخ المرضي مالتك لتوثيقه بالملف حتى نوفر وقتك وية الدكتور؟"
4. أخذ التاريخ المرضي تدريجياً (إلزامي): إذا وافق، يجب عليك سؤاله عن كافة تفاصيل التاريخ المرضي (الشكوى وتفاصيلها، متى بدأت، ما يزيدها ويخففها، الأعراض المصاحبة، الأدوية الحالية، الأمراض المزمنة، والتاريخ العائلي). اسأليه سؤالاً واحداً أو سؤالين في كل رسالة وتدرجي معه. إياكِ أن تنسي أو تتخطي أي قسم من التاريخ المرضي.
5. توثيق التاريخ المرضي: يُمنع منعاً باتاً استدعاء أداة 'submit_clinical_history' قبل أن يكمل المريض إجاباته عن جميع محاور التاريخ المرضي المذكورة أعلاه. بمجرد إكماله لجميع المحاور، استدعي الأداة لحفظها في الملف.
6. طلب المرفقات: بعد نجاح توثيق التاريخ المرضي، اسأليه فوراً بشكل صريح: "تم حفظ التاريخ المرضي. هل عندك أي تقارير، تحاليل، أو أشعة سابقة تحب ترفقها بالملف؟ تگدر ترفعها من زر المرفقات 📎 بجانب مربع الكتابة."
7. الختام: بعد إرسال المرفق (أو إذا قال لا يوجد)، اشكريه بلطف، وأكدي له أنه تم إرفاق الملفات بنجاح، وتمني له الشفاء العاجل.

قواعد عامة للذكاء وعدم التكرار ومنع الهلوسة:
- لا تكرري نفس الترحيب في كل رسالة.
- إذا عرفتِ معلومة (الاسم، العمر) لا تسألي عنها مجدداً.
- استخدمي ردوداً قصيرة، بشرية، وعفوية (مثل سكرتيرة حقيقية).
- كوني دقيقة في قراءة [تاريخ الموعد] المختار وتأكيده.
- الشخصية: لطيفة، مجاملة (استخدمي: تدلل، من عيوني). كوني متعاطفة.
- الاستنتاج التلقائي للجنس: استخدمي {"patient_gender": "F"} للأنثى أو {"patient_gender": "M"} للذكر في نهاية رسالتك ليقوم النظام بتغيير مظهرك تلقائياً.
- الموقع: ${whiteLabel?.address || "بغداد"}. (يوجد خريطة أسفل الشاشة).
- [قاعدة صارمة لمنع الهلوسة]: تخصص الدكتور وخبرته كالتالي: "طبيب ممارس وأمراض جلدية، لديه خبرة طويلة، عمل بالعراق والمراكز الصحية الحكومية 8 سنوات في سلطنة عمان و 17 سنة في مملكة البحرين، عضو جمعية أطباء البحرين سابقاً وعضو نقابة أطباء العراق." يُمنع منعاً باتاً الهلوسة بأي تخصص آخر (مثل أنف وأذن وحنجرة) أو اختراع معلومات غير موجودة هنا. أجيبي فقط بناءً على هذه المعلومات المتوفرة.`;
        
      const bossPrompt = `أنتِ الآن آلاء في وضع المدير السري (الذكاء الفائق). أنتِ ترافقين الدكتور واثق داخل لوحة التحكم الخاصة بالعيادة.
يُمنع منعاً باتاً طلب كلمة سر (لأنه تم التحقق منها مسبقاً).
أجيبي على أي سؤال علمي أو طبي أو عام يطرحه الدكتور بذكاء شديد ومباشرة.
لا تستدعي أداة فتح العيادة لأن العيادة مفتوحة بالفعل وأنتِ بداخلها.
كوني مخلصة وذكية جداً.`;

      const apiMessages = [...newMessages];
      if (hiddenApiPrompt) {
        apiMessages[apiMessages.length - 1] = { role: "user", text: hiddenApiPrompt };
      }

      const res = await fetch("/api/patient-chat", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "text/event-stream"
        },
        body: JSON.stringify({ 
          messages: apiMessages.slice(-20),
          attachments: currentAttachments,
          systemPromptOverride: bossModeActive ? bossPrompt : normalPrompt,
          userLocation: ipLocation || undefined,
          userEmail: currentUser?.email || undefined,
          patientId: sessionPatientId || activeMember?.id || undefined,
          availableSlots: availableSlots
        })
      });

      // Auto-save attachments to the patient's medical documents record
      if (currentAttachments.length > 0 && (sessionPatientId || activeMember?.id)) {
        const patientId = sessionPatientId || activeMember?.id;
        let savedCount = 0;
        for (const att of currentAttachments) {
          try {
            const saveRes = await fetch(`/api/patient/upload-document`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                patientId: patientId,
                title: att.name || "مرفق طبي من المحادثة",
                fileData: att.base64,
                fileType: att.type
              })
            });
            if (saveRes.ok) savedCount++;
          } catch (e) {
            console.error("Failed to auto-save attachment to medical record:", e);
          }
        }
        // The AI is instructed in the prompt to acknowledge file uploads and wish the patient well.
        // Therefore, we removed the hardcoded confirmMsg to prevent duplicate messages.
      }



      // Clear attachments after sending successfully
      setAttachments([]);

      if (!res.ok || !res.body) {
        setMessages(prev => [...prev, { role: "model", text: "عذراً، أواجه صعوبة في الاتصال بشبكة العيادة." }]);
        setLoading(false);
        return;
      }

      setMessages(prev => [...prev, { role: "model", text: "" }]);
      setLoading(false); 

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let done = false;
      let buffer = "";
      let fullResponseText = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          buffer += decoder.decode(value, { stream: !done });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          
          for (const line of lines) {
            if (line.trim().startsWith("data: ")) {
              const dataStr = line.trim().slice(6).trim();
              if (dataStr === "[DONE]") {
                done = true;
                if (isVoiceEnabledRef.current && fullResponseText.trim().length > 0) {
                  try {
                    if (window.speechSynthesis) {
                      // Clean markdown from text
                      const cleanText = fullResponseText.replace(/[*_#]/g, '');
                      const utterance = new SpeechSynthesisUtterance(cleanText);
                      utterance.lang = 'ar-SA';
                      utterance.rate = 0.95; // Slightly slower for more natural, human pace
                      utterance.pitch = 1.1; // Slightly higher pitch for female voice
                      
                      const voices = window.speechSynthesis.getVoices();
                      // Try to find a premium neural female voice if available
                      const arabicVoice = voices.find(v => v.lang.startsWith('ar') && (v.name.includes('Online') || v.name.includes('Premium') || v.name.includes('Hoda') || v.name.includes('Laila'))) || voices.find(v => v.lang.startsWith('ar'));
                      
                      if (arabicVoice) {
                        utterance.voice = arabicVoice;
                      }
                      
                      window.speechSynthesis.speak(utterance);
                    }
                  } catch (e) {
                    console.error("Failed to play native TTS", e);
                  }
                }
                break;
              }
              try {
                const parsed = JSON.parse(dataStr);
                if (parsed.patientId) {
                  setSessionPatientId(parsed.patientId);
                }
                if (parsed.gender) {
                  const g = parsed.gender.toUpperCase();
                  if (g.startsWith('D')) setPatientGender('D');
                  else setPatientGender(g.startsWith('M') ? 'M' : 'F');
                }
                if (parsed.require_pin) {
                  setShowPinModal(true);
                }
                if (parsed.auth_token && parsed.auth_user) {
                  localStorage.setItem("clinic_token", parsed.auth_token);
                  localStorage.setItem("clinic_user", JSON.stringify(parsed.auth_user));
                  setTimeout(() => {
                    window.location.href = "/#admin";
                    window.location.reload();
                  }, 3000);
                }
                if (parsed.action === 'PRINT_DOCUMENT' && onPrintRequest) {
                  onPrintRequest(parsed.actionData.docType, parsed.actionData.patientName);
                }
                if (parsed.action === 'GENERATE_DOCUMENT') {
                  const ev = new CustomEvent('ai_document_generated', { detail: parsed.actionData });
                  window.dispatchEvent(ev);
                }
                if (parsed.action === 'BLOCK_USER') {
                  setIsBlocked(true);
                }
                if (parsed.action === 'REQUEST_GPS_LOCATION') {
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((position) => {
                      setIpLocation(`إحداثيات دقيقة جداً (GPS): خط العرض ${position.coords.latitude}، خط الطول ${position.coords.longitude}`);
                    }, () => {
                      // Silently ignore if denied, the previous IP location will still be used.
                    });
                  }
                }
                if (parsed.text) {
                  fullResponseText += parsed.text;
                  
                  let cleanedText = fullResponseText;
                  const genderMatch = fullResponseText.match(/{\s*"(?:patient_gender|gender)"\s*:\s*"([MFD])"\s*}/i);
                  if (genderMatch) {
                    const g = genderMatch[1].toUpperCase();
                    if (g === 'D') setPatientGender('D');
                    else setPatientGender(g === 'M' ? 'M' : 'F');
                  }
                  
                  cleanedText = cleanedText.replace(/{\s*"(?:patient_gender|gender)"\s*:\s*"[MFD]"\s*}\s*/gi, "");

                  setMessages(prev => {
                    const updated = [...prev];
                    const lastMsg = updated[updated.length - 1];
                    if (lastMsg && lastMsg.role === "model") {
                      updated[updated.length - 1] = { ...lastMsg, text: cleanedText };
                    }
                    return updated;
                  });
                }
              } catch (e) {
                // Ignore incomplete JSON chunks from edge cases if any
              }
            }
          }
        }
      }

      setMessages(prev => {
        const updated = [...prev];
        const lastMsg = updated[updated.length - 1];
        if (lastMsg && lastMsg.role === "model" && lastMsg.text.trim() === "") {
          updated.pop();
        }
        return updated;
      });

    } catch (err) {
      setMessages(prev => [...prev, { role: "model", text: "عذراً، حدث خطأ في الاتصال." }]);
      setLoading(false);
    }
  };

  // Image Compression Helper
  const processAndCompressFile = (file: File) => {
    return new Promise<{name: string, type: string, base64: string}>((resolve, reject) => {
      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1200;
          let width = img.width;
          let height = img.height;

          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
            resolve({ name: file.name, type: 'image/jpeg', base64: dataUrl });
          } else {
            reject(new Error("Canvas context failed"));
          }
        };
        img.onerror = () => reject(new Error("Image load failed"));
      } else if (file.type === 'application/pdf') {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve({ name: file.name, type: file.type, base64: reader.result as string });
        reader.onerror = () => reject(new Error("FileReader failed"));
      } else {
        reject(new Error("Unsupported file type"));
      }
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      const newAtts = [];
      for (let i = 0; i < files.length; i++) {
        if (files[i].size > 10 * 1024 * 1024) continue; // max 10MB
        const processed = await processAndCompressFile(files[i]);
        newAtts.push(processed);
      }
      setAttachments(prev => [...prev, ...newAtts]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("File compression error", err);
    }
  };

  // 3. PROTECTED FUNCTION CALLING: Strictly Isolated Direct Booking
  const handleDirectBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) {
      setSlotError("الرجاء اختيار موعد أولاً.");
      return;
    }
    const activeMember = (bookingMode === 'MEMBER') ? (selectedFamilyMember || registeredPatient) : null;
    const effectiveName = activeMember ? activeMember.name : bookingForm.patientName;
    const effectivePhone = activeMember ? activeMember.phone : bookingForm.phoneNumber;
    // For MEMBER: reason is not required — triage will be handled by Alaa after booking
    const effectiveReason = (bookingMode === 'MEMBER')
      ? "سيؤخذ لاحقاً من آلاء"
      : bookingForm.reason;

    if (bookingMode !== 'MEMBER') {
      if (!effectiveName.trim() || !effectivePhone.trim() || !effectiveReason.trim()) {
        setSlotError("الرجاء ملء جميع الحقول.");
        return;
      }
      const cleanPhoneGuest = effectivePhone.replace(/\D/g, "");
      if (cleanPhoneGuest.length !== 11 || !cleanPhoneGuest.startsWith("07")) {
        setSlotError("يرجى إدخال رقم هاتف عراقي عيادي صحيح.");
        return;
      }
    }



    setIsSubmittingBooking(true);
    setSlotError(null);
    
    try {
      // Auto-infer gender if not set, so she transforms immediately
      if (!patientGender && effectiveName) {
        const firstToken = effectiveName.split(' ')[0] || "";
        const isFemale = firstToken.endsWith('ة') || firstToken.endsWith('اء') || ['مريم','زينب','نور','شهد','رند','رؤى','سجى','ضحى'].includes(firstToken);
        setPatientGender(isFemale ? 'F' : 'M');
      }

      playAiSuccessSound();

      // UI Clean up
      setBookingForm({ patientName: "", phoneNumber: "", reason: "" });
      setSelectedSlot(null);
      setShowSelector(false); 
      setBookingMode('IDLE');

      // Send the choice directly to Alaa to process
      const uiMessage = `يا هلا آلاء، لقد اخترت الموعد [${selectedSlot.displayMsg} - ${selectedSlot.dateTime}] للمريض (${effectiveName}). يرجى تثبيت الحجز.`;
      const hiddenMessage = `[طلب حجز مباشر من الواجهة]: لقد اخترت الموعد [${selectedSlot.dateTime}] للمريض (${effectiveName}) ورقم هاتفه هو (${effectivePhone}) والشكوى هي (${effectiveReason}). قم باستدعاء أداة الحجز (book_appointment) فوراً لتثبيت الموعد، ثم ابدأ بطرح أسئلة التاريخ المرضي.`;
      
      await executeSend(uiMessage, attachments, hiddenMessage);
      setAttachments([]);

    } catch (err) {
      setSlotError("حدث خطأ، يرجى المحاولة لاحقاً.");
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  const effectiveGender = (currentUser || bossModeActive || patientGender === 'D') ? patientGender : null;

  return (
    <div className={`${onClose ? "fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-slate-900/50 backdrop-blur-sm" : "min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-0 sm:p-4"} font-sans select-none`} dir="rtl">
      <div className={`w-full ${onClose ? "max-w-md h-[92dvh] sm:h-[600px] sm:max-h-[90vh] rounded-t-3xl sm:rounded-2xl" : "max-w-3xl h-[100dvh] sm:h-[85vh] rounded-none sm:rounded-2xl border-none sm:border-solid"} bg-white border border-[#E2E8F0] shadow-2xl overflow-hidden flex flex-col relative animate-in slide-in-from-bottom-10 sm:zoom-in-95 duration-300`}>
        
        {/* Header */}
        <div className={`px-3.5 py-2 sm:p-4 flex items-center justify-between shrink-0 text-white shadow-md z-10 transition-colors ${effectiveGender === 'F' ? 'bg-pink-900' : effectiveGender === 'M' ? 'bg-[#0c4a6e]' : effectiveGender === 'D' ? 'bg-purple-900' : 'bg-[#0F172A]'}`}>
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
            <div className="bg-transparent rounded-full flex items-center justify-center overflow-hidden w-9 h-9 sm:w-12 sm:h-12 shrink-0">
              <IQDoctorLogo className="w-full h-full" />
            </div>
            <div className="min-w-0">
              <h1 className="font-black text-sm sm:text-lg tracking-tight flex items-center flex-wrap gap-1 sm:gap-2 leading-tight">
                الدكتور واثق الجلبي
                <span 
                  className="text-white text-[9px] sm:text-[10px] px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md sm:rounded-lg tracking-widest font-mono font-bold uppercase shadow-md transition-all animate-pulse"
                  style={{
                    background: 'linear-gradient(270deg, #6366f1, #a855f7, #ec4899, #6366f1)',
                    backgroundSize: '200% 200%',
                    animation: 'gradientMove 3s ease infinite, pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }}
                >
                  <style>
                    {`
                      @keyframes gradientMove {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                      }
                    `}
                  </style>
                  ✨ تحدث مع آلاء
                </span>
              </h1>
              <p className="text-[11px] sm:text-xs text-slate-300 font-medium leading-none mt-1 sm:mt-0.5">آلاء - مساعدة الاستعلامات</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {currentUser ? (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="text-[10px] bg-indigo-500/30 text-indigo-100 px-2 py-1 rounded-full border border-indigo-400/30 truncate max-w-[100px] hidden sm:block font-sans" dir="ltr">
                  {currentUser.email}
                </span>
                <button
                  onClick={() => signOut(auth)}
                  className="p-1.5 sm:p-2 bg-rose-500/20 hover:bg-rose-500/40 rounded-full transition text-rose-200 hover:text-white"
                  title="تسجيل الخروج"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-1.5 bg-indigo-500/20 hover:bg-indigo-500/40 border border-indigo-400/30 rounded-full transition text-indigo-100 hover:text-white text-xs font-bold"
              >
                <LogIn className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">تسجيل الدخول / إنشاء حساب</span>
              </button>
            )}

            {onClose ? (
              <button onClick={onClose} className="p-1.5 sm:p-2 bg-white/10 hover:bg-white/20 rounded-full transition text-slate-300 hover:text-white ml-1 sm:ml-2" title="إغلاق المحادثة">
                 <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 sm:w-5 sm:h-5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            ) : (
              <a href="/" className="p-1.5 sm:p-2 bg-white/10 hover:bg-white/20 rounded-full transition text-slate-300 hover:text-white ml-1 sm:ml-2" title="العودة للواجهة الرئيسية">
                 <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 sm:w-5 sm:h-5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </a>
            )}
          </div>
        </div>

        {/* Auth Modal */}
        <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />

        {/* Chat Area Container */}
        <div className={`flex-1 relative overflow-hidden transition-colors ${effectiveGender === 'F' ? 'bg-pink-50/30' : effectiveGender === 'M' ? 'bg-sky-50/30' : effectiveGender === 'D' ? 'bg-purple-50/30' : 'bg-slate-50'}`}>
          {/* Background Alaa Image with AI Animation (Fixed Position) */}
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden flex items-center justify-center select-none">
            <div className="relative w-full h-full flex items-center justify-center opacity-80 transition-all duration-1000 ease-in-out">
              {/* Serious AI Analysis Glows & Scanners */}
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] rounded-full blur-[100px] animate-pulse opacity-50 ${effectiveGender === 'F' ? 'bg-pink-600' : effectiveGender === 'M' ? 'bg-sky-600' : effectiveGender === 'D' ? 'bg-purple-600' : 'bg-slate-400'}`}></div>
              
              {/* Radar / Core Analysis Pulsing Ring */}
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full border-4 opacity-20 motion-safe:animate-[ping_5s_cubic-bezier(0,0,0.2,1)_infinite] ${effectiveGender === 'F' ? 'border-pink-400' : effectiveGender === 'M' ? 'border-sky-400' : effectiveGender === 'D' ? 'border-purple-400' : 'border-slate-300'}`}></div>
              
              <div className={`absolute top-1/3 left-1/3 w-72 h-72 rounded-full blur-[60px] animate-bounce opacity-40 ${effectiveGender === 'F' ? 'bg-pink-500' : effectiveGender === 'M' ? 'bg-sky-500' : effectiveGender === 'D' ? 'bg-purple-500' : 'bg-slate-300'}`} style={{ animationDuration: '6s' }}></div>
              
              <img 
                src={(bossModeActive || effectiveGender === 'D') ? "/alaa_professor.png" : effectiveGender ? "/alaa_nurse_bg.png?v=3" : "/alaa_avatar.png?v=3"} 
                alt="Alaa Background" 
                className="w-full h-full object-cover sm:object-contain transform scale-110 motion-safe:animate-[pulse_4s_ease-in-out_infinite] opacity-90" 
                style={{ filter: `drop-shadow(0 0 20px rgba(0,0,0,0.2)) ${effectiveGender === 'M' ? 'hue-rotate(-90deg) saturate(1.2)' : ''}` }}
              />

              {/* Vertical AI Scanning Line */}
              {effectiveGender && (
                <div className="absolute left-0 right-0 h-1 motion-safe:animate-[bounce_3s_infinite] opacity-50" style={{
                  top: '50%',
                  background: `linear-gradient(90deg, transparent, ${effectiveGender === 'F' ? '#f472b6' : effectiveGender === 'M' ? '#38bdf8' : '#c084fc'}, transparent)`,
                  boxShadow: `0 0 15px 5px ${effectiveGender === 'F' ? '#f472b6' : effectiveGender === 'M' ? '#38bdf8' : '#c084fc'}`,
                  animationDuration: '8s'
                }}></div>
              )}
            </div>
          </div>

          {/* Scrollable Messages Area */}
          <div ref={scrollRef} className="absolute inset-0 p-6 overflow-y-auto space-y-6 z-10">

          {messages.map((msg, idx) => (
            <div key={idx} className={`relative z-10 flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'model' && (
                <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden border border-slate-200">
                  <img src={(bossModeActive || effectiveGender === 'D') ? "/alaa_professor.png" : "/alaa_avatar.png?v=3"} alt="آلاء" className="w-full h-full object-cover" style={{ filter: effectiveGender === 'M' ? 'hue-rotate(-90deg) saturate(1.2)' : 'none' }} />
                </div>
              )}
              <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed transition-colors ${
                msg.role === 'user' 
                  ? `${effectiveGender === 'F' ? 'bg-pink-600' : effectiveGender === 'M' ? 'bg-sky-600' : effectiveGender === 'D' ? 'bg-purple-600' : 'bg-indigo-600'} text-white rounded-tl-sm shadow-md` 
                  : 'bg-white text-slate-800 border border-slate-200 rounded-tr-sm shadow-sm'
              }`}>
                <div style={{ whiteSpace: 'pre-wrap' }} dir="auto">{msg.text}</div>
              </div>
              {msg.role === 'user' && (
                <div className="shrink-0 w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center border border-slate-300 mt-1">
                  <User className="w-4 h-4 text-slate-600" />
                </div>
              )}
            </div>
          ))}
          
          {loading && (
            <div className="relative z-10 flex gap-3 justify-start items-center">
              <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden border border-slate-200">
                <img src={(bossModeActive || effectiveGender === 'D') ? "/alaa_professor.png" : "/alaa_avatar.png?v=3"} alt="آلاء" className="w-full h-full object-cover" style={{ filter: effectiveGender === 'M' ? 'hue-rotate(-90deg) saturate(1.2)' : 'none' }} />
              </div>
              <div className="bg-white px-4 py-3 rounded-2xl rounded-tr-sm shadow-sm border border-slate-200 flex items-center gap-2 text-slate-500 text-xs font-medium">
                <Sparkles className={`w-3.5 h-3.5 animate-pulse ${effectiveGender === 'F' ? 'text-pink-500' : effectiveGender === 'M' ? 'text-sky-500' : effectiveGender === 'D' ? 'text-purple-500' : 'text-indigo-500'}`} />
                آلاء تقوم بالرد...
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Input & Calendar Area */}
        <div className="p-4 bg-white border-t border-slate-200 shrink-0 max-h-[50vh] overflow-y-auto scrollbar-thin">
          <div className={`flex flex-col sm:flex-row items-center justify-between gap-2 mb-2.5 text-[11px] font-black ${effectiveGender === 'F' ? 'text-pink-950 bg-pink-50/70 border-pink-100/45' : effectiveGender === 'M' ? 'text-sky-950 bg-sky-50/70 border-sky-100/45' : effectiveGender === 'D' ? 'text-purple-950 bg-purple-50/70 border-purple-100/45' : 'text-indigo-950 bg-indigo-50/70 border-indigo-100/45'} py-1.5 px-3 rounded-lg border transition-colors`}>
            <div className="text-right">
              ✨ أقرب موعد متوفر: <span className={`${effectiveGender === 'F' ? 'text-pink-700' : effectiveGender === 'M' ? 'text-sky-700' : effectiveGender === 'D' ? 'text-purple-700' : 'text-indigo-700'} transition-colors`}>{nextAvailable || "متاح للبحث"}</span>
            </div>
            <button
              type="button"
              onClick={() => {
                playAiHoverSound();
                if (bookingMode !== 'IDLE') {
                  // Already open — close everything
                  setBookingMode('IDLE');
                  setShowSelector(false);
                  setSelectedSlot(null);
                  setBookingSuccess(null);
                  setSlotError(null);
                } else if (currentUser) {
                  // Logged-in user → MEMBER flow
                  // If multiple family members → show picker first, else go direct
                  const firstMember = selectedFamilyMember || familyMembers[0] || null;
                  if (firstMember) {
                    setBookingForm({ patientName: firstMember.name || '', phoneNumber: firstMember.phone || '', reason: '' });
                  }
                  setBookingMode('MEMBER');
                  // Always show the picker to confirm the patient name
                  setShowSelector(false);
                } else {
                  // Not logged in → show gate
                  setBookingMode('GATE');
                  setShowSelector(false);
                }
              }}
              className={`${effectiveGender === 'F' ? 'bg-pink-600 hover:bg-pink-700' : effectiveGender === 'M' ? 'bg-sky-600 hover:bg-sky-700' : effectiveGender === 'D' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700'} text-white px-3 py-1 rounded text-[10px] font-bold transition select-none flex items-center gap-1 cursor-pointer shadow-sm`}
            >
              📅 {bookingMode !== 'IDLE' ? "إغلاق نافذة الحجز المباشر" : "قم بالحجز المباشر"}
            </button>
          </div>

          {/* === GATE: Choose booking track (non-logged-in users) === */}
          {bookingMode === 'GATE' && (
            <div className="bg-white border border-indigo-200 rounded-xl p-4 mb-3 animate-in fade-in slide-in-from-bottom-2 duration-200 text-right shadow-md">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-3">
                <span className="text-[11px] font-extrabold text-indigo-700 flex items-center gap-1">📅 حجز موعد — اختر طريقة المتابعة</span>
                <button type="button" onClick={() => setBookingMode('IDLE')} className="text-[10px] text-slate-400 hover:text-rose-600 font-bold">إغلاق ×</button>
              </div>

              {/* Option 1: Login / Register */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-3 mb-2.5">
                <div className="flex items-start gap-2.5 mb-3">
                  <span className="text-2xl shrink-0">🔐</span>
                  <div>
                    <p className="text-[11px] font-extrabold text-indigo-900 mb-1">لديّ حساب / أريد إنشاء حساب</p>
                    <ul className="text-[10px] text-indigo-700 space-y-0.5 font-medium">
                      <li>✅ ما تحتاج تدخل بياناتك كل مرة</li>
                      <li>✅ تشوف مواعيدك في أي وقت</li>
                      <li>✅ ملفك الطبي يُحفظ تلقائياً</li>
                    </ul>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setIsAuthModalOpen(true); setBookingMode('IDLE'); }}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-[11px] py-2 rounded-lg transition flex items-center justify-center gap-1.5"
                >
                  <LogIn className="w-3.5 h-3.5" /> تسجيل الدخول / إنشاء حساب مجاني
                </button>
              </div>

            </div>
          )}

          {/* === FAMILY MEMBER PICKER: shown for MEMBER === */}
          {bookingMode === 'MEMBER' && !showSelector && familyMembers.length > 0 && (
            <div className="bg-white border border-indigo-200 rounded-xl p-4 mb-3 animate-in fade-in slide-in-from-bottom-2 duration-200 text-right shadow-md">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-3">
                <span className="text-[11px] font-extrabold text-indigo-700 flex items-center gap-1">👨‍👩‍👧 اختر الفرد المراد حجز الموعد له:</span>
                <button type="button" onClick={() => setShowSelector(true)} className="text-[10px] text-slate-400 hover:text-rose-600 font-bold">إغلاق ×</button>
              </div>
              <div className="space-y-2 max-h-[130px] overflow-y-auto scrollbar-thin pl-1 pb-1">
                {familyMembers.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => {
                      setSelectedFamilyMember(member);
                      setRegisteredPatient(member);
                      setBookingForm({ patientName: member.name || '', phoneNumber: member.phone || '', reason: '' });
                      setShowSelector(true);
                      
                      const confirmMsg: Message = {
                        role: "model",
                        text: `عيني، تم اختيار ملف (${member.name}). الرجاء تحديد الوقت المناسب من القائمة بالأسفل حتى أثبت الحجز.`
                      };
                      setMessages(prev => [...prev, confirmMsg]);
                      
                      if (isVoiceEnabledRef.current && window.speechSynthesis) {
                        try {
                          const utterance = new SpeechSynthesisUtterance(`عيني، تم اختيار ملف ${member.name}. الرجاء تحديد الوقت المناسب من القائمة بالأسفل حتى أثبت الحجز.`);
                          utterance.lang = 'ar-SA';
                          utterance.rate = 0.95;
                          utterance.pitch = 1.1;
                          const voices = window.speechSynthesis.getVoices();
                          const arabicVoice = voices.find(v => v.lang.startsWith('ar') && (v.name.includes('Online') || v.name.includes('Premium') || v.name.includes('Hoda') || v.name.includes('Laila'))) || voices.find(v => v.lang.startsWith('ar'));
                          if (arabicVoice) utterance.voice = arabicVoice;
                          window.speechSynthesis.speak(utterance);
                        } catch (e) {}
                      }
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border-2 transition text-right cursor-pointer ${
                      selectedFamilyMember?.id === member.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 bg-slate-50 hover:border-indigo-300 hover:bg-indigo-50/50'
                    }`}
                  >
                    <div className="text-right">
                      <p className="text-[12px] font-extrabold text-slate-800">{member.name}</p>
                      {member.dob && <p className="text-[10px] text-slate-500 font-medium">سنة الميلاد: {member.dob}</p>}
                    </div>
                    <span className="text-lg shrink-0">{selectedFamilyMember?.id === member.id ? '✅' : '👤'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* === BOOKING PANEL: shown for MEMBER (logged-in) and FAMILY (guest) === */}
          {showSelector && (bookingMode === 'MEMBER' || bookingMode === 'FAMILY') && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-3 space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-200 text-right max-h-[35vh] overflow-y-auto scrollbar-thin">
              <div className="font-black text-[11px] text-slate-800 flex items-center justify-between border-b border-slate-200 pb-2">
                <span className={`${effectiveGender === 'F' ? 'text-pink-600' : effectiveGender === 'M' ? 'text-sky-600' : effectiveGender === 'D' ? 'text-purple-600' : 'text-indigo-600'} font-extrabold flex items-center gap-1 transition-colors`}>
                  {bookingMode === 'MEMBER' ? `📅 حجز بحساب: ${currentUser?.email}` : '📅 نظام الحجز المباشر السريع (بدون انتظار):'}
                </span>
                <button type="button" onClick={() => { setShowSelector(false); setBookingMode('IDLE'); setSelectedSlot(null); setBookingSuccess(null); setSlotError(null); }} className="text-[10px] text-slate-400 hover:text-rose-600 font-bold">إغلاق ×</button>
              </div>

              {bookingSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 p-2.5 rounded-lg text-xs font-bold text-center">
                  ✅ تم تثبيت موعدك بنجاح في قاعدة البيانات: {bookingSuccess}
                </div>
              )}

              {isLoadingSlots ? (
                <div className="flex flex-col items-center justify-center py-4 gap-1.5">
                  <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${effectiveGender === 'F' ? 'border-pink-600' : effectiveGender === 'M' ? 'border-sky-600' : effectiveGender === 'D' ? 'border-purple-600' : 'border-indigo-600'} transition-colors`}></div>
                  <span className="text-[10px] text-slate-500 font-bold">جاري تحميل المواعيد...</span>
                </div>
              ) : slotError ? (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 p-2 rounded-lg text-xs font-bold text-center">
                  ⚠️ {slotError}
                </div>
              ) : (
                <div className="space-y-3">
                  {!bookingSuccess && (
                     <>
                     <div className="flex flex-col gap-1.5 mb-2">
                        <label className="text-[10px] text-slate-600 font-bold">اختيار تاريخ محدد لتصفح المواعيد:</label>
                        <input
                           type="date"
                           value={selectedDateFilter}
                           onChange={(e) => setSelectedDateFilter(e.target.value)}
                           className={`text-xs p-1.5 border border-slate-200 rounded focus:ring-1 ${effectiveGender === 'F' ? 'focus:ring-pink-500 focus:border-pink-500' : effectiveGender === 'M' ? 'focus:ring-sky-500 focus:border-sky-500' : effectiveGender === 'D' ? 'focus:ring-purple-500 focus:border-purple-500' : 'focus:ring-indigo-500 focus:border-indigo-500'} text-right transition-colors`}
                           min={new Date().toISOString().split('T')[0]}
                        />
                     </div>
                    <div className="grid grid-cols-2 gap-1.5 max-h-[110px] overflow-y-auto pr-1">
                      {availableSlots.map((slot, sIdx) => {
                        const isSelected = selectedSlot?.dateTime === slot.dateTime;
                        return (
                          <button
                            key={sIdx}
                            type="button"
                            onClick={() => {
                              setSelectedSlot(slot);
                              setInput(`أريد حجز الموعد: ${slot.displayMsg} - [${slot.dateTime}]`);
                              playAiHoverSound();
                            }}
                            className={`text-[10px] font-bold py-2 px-2 rounded-lg border text-center transition-all flex items-center justify-between cursor-pointer ${
                              isSelected
                                ? `${effectiveGender === 'F' ? 'bg-pink-600 border-pink-600' : effectiveGender === 'M' ? 'bg-sky-600 border-sky-600' : effectiveGender === 'D' ? 'bg-purple-600 border-purple-600' : 'bg-indigo-600 border-indigo-600'} text-white shadow-sm`
                                : `bg-white border-slate-200 text-slate-700 ${effectiveGender === 'F' ? 'hover:border-pink-400 hover:bg-pink-50' : effectiveGender === 'M' ? 'hover:border-sky-400 hover:bg-sky-50' : effectiveGender === 'D' ? 'hover:border-purple-400 hover:bg-purple-50' : 'hover:border-indigo-400 hover:bg-indigo-50'}`
                            }`}
                          >
                            <span className="flex-1 min-w-0 truncate text-right">{slot.displayMsg}</span>
                          </button>
                        );
                      })}
                    </div>
                    </>
                  )}

                  {selectedSlot && !bookingSuccess && (
                    <div className={`border-t ${effectiveGender === 'F' ? 'border-pink-100' : effectiveGender === 'M' ? 'border-sky-100' : effectiveGender === 'D' ? 'border-purple-100' : 'border-indigo-100'} pt-3 space-y-2.5 animate-in fade-in duration-200 transition-colors`}>
                      <div className={`${effectiveGender === 'F' ? 'bg-pink-50 text-pink-900 border-pink-100/50' : effectiveGender === 'M' ? 'bg-sky-50 text-sky-900 border-sky-100/50' : effectiveGender === 'D' ? 'bg-purple-50 text-purple-900 border-purple-100/50' : 'bg-indigo-50 text-indigo-900 border-indigo-100/50'} p-2 rounded-lg text-xs font-bold text-center border transition-colors`}>
                        {selectedSlot.displayMsg}
                      </div>

                      <button type="button" onClick={handleDirectBooking} disabled={isSubmittingBooking} className={`w-full ${effectiveGender === 'F' ? 'bg-pink-600 hover:bg-pink-700' : effectiveGender === 'M' ? 'bg-sky-600 hover:bg-sky-700' : effectiveGender === 'D' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700'} text-white font-extrabold text-[12px] py-2 px-3 rounded-lg transition-colors shadow-sm disabled:bg-slate-300 flex items-center justify-center gap-1.5 cursor-pointer`}>
                        {isSubmittingBooking ? <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full"></div> : "✓ تأكيد الحجز مباشرة"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Attachments UI */}
          {attachments.length > 0 && (
            <div className="flex gap-2 p-2 bg-slate-50 border-t border-slate-200 overflow-x-auto items-center">
              {attachments.map((att, idx) => (
                <div key={idx} className="relative w-16 h-16 bg-white border border-slate-300 rounded-lg shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                  {att.type.startsWith('image') ? (
                    <img src={att.base64} alt="attachment" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-bold text-slate-500">PDF</span>
                  )}
                  <button type="button" onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))} className="absolute top-0 right-0 bg-rose-500 text-white rounded-bl-lg p-0.5" title="حذف المرفق">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => executeSend(input, attachments)}
                disabled={loading || isBlocked}
                className={`shrink-0 h-10 px-4 mr-2 rounded-lg font-bold text-white text-xs shadow-sm transition-colors ${effectiveGender === 'F' ? 'bg-pink-600 hover:bg-pink-700' : effectiveGender === 'M' ? 'bg-sky-600 hover:bg-sky-700' : effectiveGender === 'D' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700'} disabled:bg-slate-300 disabled:cursor-not-allowed`}
              >
                تأكيد المرفقات ✓
              </button>
            </div>
          )}

          {/* Security PIN Modal */}
          {showPinModal && (
            <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md z-50 flex items-center justify-center p-4 rounded-xl">
              <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center animate-in zoom-in-95 duration-200">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-rose-50">
                  <Shield className="w-8 h-8 text-rose-600" />
                </div>
                <h3 className="text-lg font-black text-slate-800 mb-2">التحقق الأمني الطارئ</h3>
                <p className="text-xs text-slate-500 font-bold mb-4">آلاء تشتبه بوجود محاولة اختراق.. يرجى إدخال رمز الـ PIN الخاص بك لإثبات هويتك دكتور واثق.</p>
                <input
                  type="password"
                  maxLength={4}
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value)}
                  placeholder="----"
                  disabled={isLocked}
                  className={`w-full text-center tracking-[1em] font-mono text-2xl py-3 rounded-lg border focus:outline-none mb-3 ${isLocked ? 'bg-slate-100 border-rose-300 text-rose-500 cursor-not-allowed' : 'border-slate-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200'}`}
                  dir="ltr"
                />
                {pinError && <p className="text-xs text-rose-600 font-bold mb-3">{pinError}</p>}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowPinModal(false)}
                    className="flex-1 py-2 rounded-lg font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={verifyEmergencyPin}
                    disabled={verifyingPin || pinInput.length < 4 || isLocked}
                    className="flex-1 py-2 rounded-lg font-bold text-white bg-rose-600 hover:bg-rose-700 transition disabled:opacity-50"
                  >
                    {verifyingPin ? "جاري التحقق..." : "تأكيد الهوية"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSend} className="relative flex items-center max-w-4xl mx-auto pt-2">
            <input 
              type="file"
              ref={fileInputRef}
              accept="image/*,application/pdf"
              className="hidden"
              multiple
              onChange={handleFileChange}
            />
            <button 
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 sm:p-2 rounded-full transition shadow-sm bg-slate-200 hover:bg-slate-300 text-slate-600"
              title="إرفاق صورة أو تقرير طبي"
            >
              <Paperclip className="w-5 h-5 sm:w-4 sm:h-4" />
            </button>
            <input 
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isBlocked ? "مغلق أمنياً..." : "تحدث المباشر مع آلاء..."}
              disabled={loading || isBlocked}
              className={`w-full bg-slate-100 border border-slate-300 text-slate-800 text-base sm:text-sm pr-24 pl-14 sm:pr-20 sm:pl-12 py-3.5 rounded-xl outline-none focus:border-[1.5px] ${effectiveGender === 'F' ? 'focus:border-pink-500' : effectiveGender === 'M' ? 'focus:border-sky-500' : effectiveGender === 'D' ? 'focus:border-purple-500' : 'focus:border-indigo-500'} focus:bg-white transition-all disabled:opacity-50 ${isBlocked ? 'bg-rose-100 cursor-not-allowed border-rose-500 placeholder-rose-500 text-rose-900 font-bold' : ''}`}
            />
            <button 
              type="button"
              onClick={toggleListen}
              className={`absolute right-12 sm:right-11 top-1/2 -translate-y-1/2 p-2.5 sm:p-2 rounded-full transition shadow-sm ${isListening ? 'bg-rose-100 text-rose-600 animate-pulse' : 'text-slate-500 hover:bg-slate-200'}`}
              title={isListening ? "جاري الاستماع..." : "تحدث بالصوت"}
            >
              <Mic className="w-5 h-5 sm:w-4 sm:h-4" />
            </button>
            <button 
              id="chatbot-send-btn"
              type="submit"
              disabled={loading || isBlocked || (!input.trim() && attachments.length === 0)}
              className={`absolute left-2 top-1/2 -translate-y-1/2 p-2.5 sm:p-2 ${isBlocked ? 'bg-rose-600 hover:bg-rose-700' : effectiveGender === 'F' ? 'bg-pink-600 hover:bg-pink-700' : effectiveGender === 'M' ? 'bg-sky-600 hover:bg-sky-700' : effectiveGender === 'D' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700'} disabled:bg-slate-300 text-white rounded-full transition shadow-sm rtl-flip`}
            >
              <Send className="w-5 h-5 sm:w-4 sm:h-4 -scale-x-100 transform" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

